import { Fragment, memo, ReactElement, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  IconButton,
  Typography,
  useEventCallback,
} from '@mui/material';
import { ExpandMore, Print } from '@mui/icons-material';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { FormikProps } from 'formik';
import { ViewStepVM } from 'civil-status/view-models/step-form';
import { HttpResponse } from 'shell/models/http-response';
import { DateTime } from 'luxon';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { noop } from 'shell/utils/noop';
import { BaseTableRows, HeadCell } from 'shell/components/shared/table/types';

const headCells: HeadCell<EventoAnnotazioneAtto, keyof EventoAnnotazioneAtto>[] = [
  {
    id: 'dataFormazione',
    label: 'Data Formazione',
  },
  {
    id: 'idAnsc',
    label: 'id Ansc',
  },
  {
    id: 'composizioneCompleta',
    label: 'Composizione Completa',
  },
  {
    id: 'action',
    label: 'Azioni',
  },
];

type EventoAnnotazioneAtto = {
  idAnsc: string;
  dataFormazione: string;
  composizioneCompleta: string;
  action: ReactElement;
} & BaseTableRows;

type EventoFromApi = {
  idAnsc: string;
  dataFormazione?: string;
  dataformazione?: string;
  composizioneCompleta?: string;
};

interface ListaAnnotazioniResponse {
  eventi?: EventoFromApi[];
  consultazioneANSCResponse?: {
    eventi?: EventoFromApi[];
    evento?: EventoFromApi | EventoFromApi[];
    modelEventoDTO?: EventoFromApi[];
  };
}

interface Props {
  formContext: FormikProps<ViewStepVM>;
  onPreviewByIdAnsc: (idAnsc: string) => void;
  idAnscFromRoute?: string;
}

const AttoAnnotationsAccordionComponent = ({
  formContext,
  onPreviewByIdAnsc,
  idAnscFromRoute,
}: Props): React.ReactElement | null => {
  const [open, setOpen] = useState(false);

  // stato per controllare la tabella
  const [tableProps, setTableProps] = useState<Record<string, number>>({
    page: 0,
    rowsPerPage: 10,
  });

  const idAnscAtto = useMemo(
    () => idAnscFromRoute || formContext.values?.idAnsc || '',
    [idAnscFromRoute, formContext.values?.idAnsc]
  );

  const [annotazioniBackend, getAnnotazioniBackend] = useHttpFetch<
    HttpResponse<ListaAnnotazioniResponse>,
    EventoAnnotazioneAtto[]
  >(`${__ACT_API_URL__}/consultazione/evento`, {
    onBootstrap: false,
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
      data: {
        criteriRicercaEvento: {
          idAnsc: idAnscAtto,
          idTipoContenuto: 0,
        },
      },
    },
    transformer: (response) => {
      const resp = response?.data?.payload?.consultazioneANSCResponse ?? {};
      const raw = resp.eventi ?? resp.evento ?? resp.modelEventoDTO ?? [];

      const arr: EventoFromApi[] = Array.isArray(raw) ? raw : [raw];

      const eventi: EventoAnnotazioneAtto[] = arr.map((e) => ({
        idAnsc: e.idAnsc,
        dataFormazione: e.dataformazione
          ? DateTime.fromISO(e.dataformazione).toFormat('dd/MM/yyyy')
          : '',
        composizioneCompleta: e.composizioneCompleta ?? '',
        action: (
          <IconButton onClick={() => onPreviewByIdAnsc(e.idAnsc)}>
            <Print />
          </IconButton>
        ),
      }));

      eventi.sort((a, b) => a.idAnsc.localeCompare(b.idAnsc));
      return eventi;
    },
  });

  useEffect(() => {
    if (!idAnscAtto) {
      return;
    }
    getAnnotazioniBackend();
  }, [idAnscAtto]);

  const annotazioni = (annotazioniBackend.data as EventoAnnotazioneAtto[]) || [];

  const onChangePage = useEventCallback((page: number) => {
    setTableProps({
      page,
      rowsPerPage: tableProps.rowsPerPage,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setTableProps({
      page: 0,
      rowsPerPage,
    });
  });

  if (!annotazioni.length) return null;

  return (
    <Fragment>
      <Accordion expanded={open} onChange={() => setOpen((v) => !v)}>
        <AccordionSummary
          aria-controls="accordion-atto-annotations-content"
          id="accordion-atto-annotations-header"
          expandIcon={<ExpandMore />}
        >
          <Typography style={{ display: 'flex', alignItems: 'center' }}>Annotazioni</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Table
            ariaLabel="Lista annotazioni"
            page={tableProps.page || 0}
            rowsPerPage={tableProps.rowsPerPage || 10}
            body={annotazioni}
            count={annotazioni.length}
            head={headCells}
            onSort={noop}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            orders={{}}
            isNotLayoutFixed
            noSort
            hideButtons
          />
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

AttoAnnotationsAccordionComponent.displayName = 'Atto Annotations Accordion Component';

const AttoAnnotationsAccordion = memo(AttoAnnotationsAccordionComponent);
export default AttoAnnotationsAccordion;
